// © Microsoft Corporation. All rights reserved.

import { PageMeasure, PerfTimeline } from "./PerfTimeline";

import { getPageTimings } from "./PageTimings";
import { logger } from "../logging";

const pageTimings = getPageTimings();

/**
 * Utility class to keep track of how many times a key has appeared in order to
 * get a uniquely qualified name back to be used in a dictionary.
 */
class KeyCounter {

    /** Lookup of counts for the given key */
    private timelineCount: { [name: string]: number } = {};

    /** Gets the qualified key name */
    public qualifyKey(timelineName: string): string {

        // get the previous count
        const count = this.getCount(timelineName);

        // if we've called it previously append the count as suffix
        // to identify it uniquely.
        if (count <= 1) {
            return timelineName;
        }

        return timelineName + count;
    }

    /** Resets the counter state to initial values */
    public reset(): void {
        this.timelineCount = {};
    }

    /** Gets the key's appearence count */
    private getCount(timelineName: string): number {

        // get the previous count
        const count = this.timelineCount[timelineName] || 0;

        // update the new count
        return this.timelineCount[timelineName] = count + 1;
    }
}

/**
 * Unique key qualifier for distinctly identifying instances of perf timelines
 * across multiple instances of the same or different experience types.
 * For example, ContentPreview, ContentPreview2, ContentPreview3, etc.
 */
const timelineKeyCount = new KeyCounter();

/** Suffix for start markers */
const startSuffix = "-start";

/** Suffix for end markers */
const endSuffix = "-end";

/**
 * This class can be used to aggregate a list of perf markers and their transitions.
 */
export class PerfTimelineImpl implements PerfTimeline {

    /**
     * Unique key qualifier for metric names that appear multiple times.
     * For example, render, render2, render3, etc.
     */
    private readonly markerCount: KeyCounter = new KeyCounter();

    /**
     * List of in-memory logged markers for this perf-timeline object
     */
    private readonly measures: Array<Omit<PageMeasure, "timelineName">> = [];

    /**
     * Constructor of a {PerfTimeline} instance
     * @param timelineName - the name of the perf timeline. Note that if you don't know the name
     *  of a perf timeline at construction time, it can later be renamed to a known name later via
     *  the 'rename' method.
     * @param instanceId - this specific instance's ID. Used during a renaming operation to idenfity any previous isntance.
     * @param addPerfMarks - Flag to indicate whether to log perf marks with the native performance API.
     * @param addPageTimings - Flag to indicate whether to log the perf marks into the _pageTimings object.
     * @param async - Flag to mark this timeline as an async timeline, so by default all measures under
     *  this timeline will show under a new process line.
     */
    public constructor(
        public timelineName: string,
        public readonly instanceId: string,
        private readonly addPerfMarks: boolean,
        private readonly addPageTimings: boolean,
        private readonly async?: boolean
    ) {
        this.timelineName = timelineKeyCount.qualifyKey(timelineName);
    }

    /**
     * Renames this instances of the timeline to the new given name.
     * This is useful when the timeline name is not known until after some asynchronous operation is completed,
     * but the measuring operation required to be started before the name was known.
     * @param timelineName - the new name of the timeline.
     */
    public rename(timelineName: string): void {

        if (this.timelineName === timelineName) {
            return;
        }

        if (this.addPageTimings) {
            // if we're adding page timings, then make sure we delete the measures under the old name and add the new ones
            this.measures.forEach(measure => {
                const oldMeasureName = this.qualifyMeasureName(measure.measureName);
                delete pageTimings[oldMeasureName + startSuffix];
                delete pageTimings[oldMeasureName];
            });
        }

        this.timelineName = timelineName;

        if (this.addPageTimings) {
            // add back the page timings under the new timeline name
            this.measures.forEach(measure => {
                const newMeasureName = this.qualifyMeasureName(measure.measureName);
                pageTimings[newMeasureName + startSuffix] = measure.from;
                pageTimings[newMeasureName] = measure.to - measure.from;
            });
        }
    }

    /**
     * Marks the start of a timeline measurement.
     * @param coreMarkerName - the name of the core marker. This value is usually invariant
     *      from instance to instance (i.e., init, config, render)
     * @param dynamicMeasureName - the optional additional identifying name to give for the measure.
     *      This value can be used to identify dynamic values across instances. For example the
     *      HREF of the config. This can also later be updated to a new name when calling the 'endMeasure'
     *      method.
     * @param async - flag to indicate whether this is an async measure or not.
     * @returns An object with the following properties:
     *      - endMeasure - method that should be used to mark the end the measure.
     */
    public startMeasure(coreMarkerName: string, dynamicMeasureName?: string, async?: boolean): { endMeasure: (updatedDynamicMeasureName?: string) => void } {

        const startTime = performance.now();

        const timelineNameAtStart = this.timelineName;
        let measureNameCore = this.formatMeasureName(coreMarkerName, dynamicMeasureName);
        let measureName = this.qualifyMeasureName(measureNameCore);

        const startMarkerName = measureName + startSuffix;
        this.singleMark(startMarkerName);

        if (this.addPageTimings) {
            pageTimings[startMarkerName] = startTime;
        }

        return {
            /**
             * Ends the given measure we started
             */
            endMeasure: (updatedDynamicMeasureName?: string) => {

                const endTime = performance.now();

                // flag to indicate whether we need to re-qualify the measure name based on frequency. Note that we only
                // set this to true if the timeline name has changed since start, or we have a new dynamic name at the end.
                const requalifyMeasureName = updatedDynamicMeasureName
                    || (timelineNameAtStart !== this.timelineName);

                if (requalifyMeasureName) {
                    // if by the time we're ending the measure, we have a new dynamic name for the measurement, use that as the final measure name.
                    measureNameCore = this.formatMeasureName(coreMarkerName, updatedDynamicMeasureName);
                    measureName = this.qualifyMeasureName(measureNameCore);
                }

                const endMarkerName = measureName + endSuffix;

                this.singleMark(endMarkerName);
                this.measure(measureName, startMarkerName, endMarkerName);

                // Note we only save the partial metric without the timeline name, so we can use the final timeline name
                // if any rename operations happen while initial measurements have started without the final timeline name
                this.measures.push({
                    measureName: measureNameCore,
                    async: (async != null) ? async : this.async,
                    from: startTime,
                    to: endTime
                });

                if (this.addPageTimings) {
                    pageTimings[measureName] = endTime - startTime;
                }
            }
        };
    }

    /**
     * Returns the list of page measures for this timeline object
     * @returns {Array<PageMeasure>} containing the fully qualified list of metric names for this perf timeline.
     */
    public getPageMeasures(): Array<PageMeasure> {
        return this.measures.map(measure => ({
            ...measure,
            timelineName: this.timelineName
        }));
    }

    /**
     * Instruments the start/begin for the given function with a given binding context
     * @param funcName - the identifying name of the function
     * @param func - the function instance to measure
     * @param bindingContext - the optional context from which to invoke the function
     * @returns the instrumented function that will track the start/end of the given function.
     */
    public instrumentFunc<TFunc extends Function>(funcName: string, func: TFunc, bindingContext?: Object): TFunc {
        const wrapper = (...args: unknown[]): unknown => {
            const endMeasure = this.startMeasure(funcName).endMeasure;
            try {
                return func.apply(bindingContext, args);
            } finally {
                endMeasure();
            }
        };

        return wrapper as unknown as TFunc;
    }

    /**
     * Instruments the start/begin for the given async-style function
     * @param promiseName - the identifying name of the promise
     * @param getPromise - the promise getter that returns the promise to measure. This is needed
     *  so that the start measurement can occur before the promise starts executing.
     * @param async - flag to indicate whether to measure this promise as an async timeline
     *  or a synchronous one. Please note that if you specify this as true, and other measurements
     *  in the timeline are synchronous, the async measurements will appear as a separate timeline
     *  and will not be next to the synchronous one. If unsure, leave undefined.
     * @returns the wrapped promise that tracks the start/end of the promise execution.
     */
    public async instrumentPromise<TResult>(promiseName: string, getPromise: () => Promise<TResult>, async?: boolean): Promise<TResult> {

        const endMeasure = this.startMeasure(promiseName, null, async).endMeasure;
        try {
            return await getPromise();
        } finally {
            endMeasure();
        }
    }

    /**
     * Prefixes the given measure name with the current timeline name
     * @param measureName - the measure name to qualify
     * @returns the timeline name prefixed measure name.
     */
    private qualifyMeasureName(measureName: string): string {
        return `${this.timelineName}-${measureName}`;
    }

    /**
     * Returns the formatted measure name with it's constiuent parts
     * @param coreMeasureName - the core marker name
     * @param dynamicMeasureName - optional dynamic measure name
     * @returns the formatted marker name
     */
    private formatMeasureName(coreMeasureName: string, dynamicMeasureName?: string): string {
        const dynamicMeasureSuffix = dynamicMeasureName ? ("-" + dynamicMeasureName) : "";
        return this.markerCount.qualifyKey(`${coreMeasureName}${dynamicMeasureSuffix}`);
    }

    /**
     * Helper to add a single mark with the performance API
     * @param markerName - the name of the marker to add.
     */
    private singleMark(markerName: string): void {
        if (this.addPerfMarks) {
            performance.mark(markerName);
        }
    }

    /**
     * Helper to calculate a performance measurement against the performance API
     * @param measureName - the name of the measure
     * @param startMarker - the name of the start marker
     * @param endMarker - the name of the end marker
     */
    private measure(measureName: string, startMarker: string, endMarker: string): void {
        if (this.addPerfMarks) {
            try {
                // this can throw if start/end markers were not available for some reason
                performance.measure(measureName, startMarker, endMarker);
            } catch (ex) {
                logger.logError(ex);
            }
        }
    }
}

/**
 * Test export for resetting the timeline name counter
 */
export function _resetTimelineKeyCounter(): void {
    timelineKeyCount.reset();
}
